/**
 * Hook for unified card lookup preferring full data when available.
 *
 * Use this hook for:
 * - Deck card display and operations (full data preferred)
 * - Collection card display (full data preferred)
 * - On-demand card fetching when browsing editions
 *
 * For card discovery/search (300k cards), use useCardIndex instead.
 *
 * Two-tier card data architecture:
 * 1. fullCardCacheAtom - Full ScryfallCard data for user's cards (Firestore)
 * 2. cardIndexByIdAtom - MinimalCard index for all cards (CDN)
 *
 * See docs/architecture.md for details on the card data flow.
 */
import { useCallback, useRef } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { cardIndexByIdAtom, type MinimalCard } from '@/services/cardDatabase';
import { getCardById, getCardsByIds } from '@/services/cards';
import { addToFullCardCache, isIndexedDBSupported } from '@/services/indexedDbCache';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { ScryfallCard } from '@/types/card';

/**
 * Unified card lookup hook preferring full data when available.
 * Falls back to MinimalCard for discovery or during initial load.
 *
 * @example
 * ```tsx
 * const { getCard, getFullCard, hasFullCard, prefetchCard } = useCardLookup();
 *
 * // Get best available data (full with minimal fallback)
 * const card = getCard(scryfallId);
 *
 * // Get full data only (null if not in cache)
 * const fullCard = getFullCard(scryfallId);
 *
 * // Check if full data is available
 * if (hasFullCard(scryfallId)) { ... }
 *
 * // Prefetch full data for a card
 * await prefetchCard(scryfallId);
 * ```
 */
export function useCardLookup() {
  const [fullCardCache, setFullCardCache] = useAtom(fullCardCacheAtom);
  const cardIndexById = useAtomValue(cardIndexByIdAtom);

  // Track in-flight fetches to avoid duplicate requests
  const fetchingRef = useRef<Set<string>>(new Set());

  /**
   * Get best available card data.
   * Returns ScryfallCard if in cache, otherwise MinimalCard from index.
   * Returns null if card doesn't exist in either source.
   */
  const getCard = useCallback(
    (scryfallId: string): ScryfallCard | MinimalCard | null => {
      return fullCardCache.get(scryfallId) ?? cardIndexById.get(scryfallId) ?? null;
    },
    [fullCardCache, cardIndexById],
  );

  /**
   * Get full card data only (for cards that should have it in cache).
   * Returns null if not in full card cache.
   */
  const getFullCard = useCallback(
    (scryfallId: string): ScryfallCard | null => {
      return fullCardCache.get(scryfallId) ?? null;
    },
    [fullCardCache],
  );

  /**
   * Check if we have full card data for a given ID.
   */
  const hasFullCard = useCallback(
    (scryfallId: string): boolean => {
      return fullCardCache.has(scryfallId);
    },
    [fullCardCache],
  );

  /**
   * Prefetch full card data from Firestore and add to cache.
   * Use this when navigating to a card that might not be in the cache
   * (e.g., browsing editions in the card details panel).
   *
   * - Returns immediately if card is already in cache
   * - Deduplicates concurrent requests for the same card
   * - Persists fetched cards to IndexedDB for future sessions
   *
   * @param scryfallId - The Scryfall ID of the card to prefetch
   * @returns The full card data, or null if fetch failed
   */
  const prefetchCard = useCallback(
    async (scryfallId: string): Promise<ScryfallCard | null> => {
      // Already in cache - return immediately
      if (fullCardCache.has(scryfallId)) {
        return fullCardCache.get(scryfallId)!;
      }

      // Already being fetched - wait for it
      if (fetchingRef.current.has(scryfallId)) {
        // Poll until it appears in cache or give up after 5 seconds
        return new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 50; // 50 * 100ms = 5 seconds
          const interval = setInterval(() => {
            attempts++;
            if (fullCardCache.has(scryfallId)) {
              clearInterval(interval);
              resolve(fullCardCache.get(scryfallId)!);
            } else if (attempts >= maxAttempts || !fetchingRef.current.has(scryfallId)) {
              clearInterval(interval);
              resolve(null);
            }
          }, 100);
        });
      }

      // Mark as fetching
      fetchingRef.current.add(scryfallId);

      try {
        const card = await getCardById(scryfallId);

        if (card) {
          // Add to cache
          setFullCardCache((prevCache) => {
            const newCache = new Map(prevCache);
            newCache.set(scryfallId, card);
            return newCache;
          });

          // Persist to IndexedDB
          if (isIndexedDBSupported()) {
            await addToFullCardCache([card]);
          }
        }

        return card;
      } catch (error) {
        console.error(`[useCardLookup] Failed to prefetch card ${scryfallId}:`, error);
        return null;
      } finally {
        fetchingRef.current.delete(scryfallId);
      }
    },
    [fullCardCache, setFullCardCache],
  );

  /**
   * Batch prefetch full card data for multiple IDs.
   * Deduplicates against already-cached IDs and in-flight fetches.
   * Fetches missing cards from Firestore in batches, updates the atom,
   * and persists to IndexedDB.
   *
   * @param scryfallIds - Array of Scryfall IDs to prefetch
   */
  const prefetchCards = useCallback(
    async (scryfallIds: string[]): Promise<void> => {
      // Deduplicate against cache and in-flight fetches
      const missingIds = scryfallIds.filter((id) => !fullCardCache.has(id) && !fetchingRef.current.has(id));

      if (missingIds.length === 0) return;

      // Mark all as fetching
      for (const id of missingIds) {
        fetchingRef.current.add(id);
      }

      try {
        const fetchedCards = await getCardsByIds(missingIds);

        if (fetchedCards.size > 0) {
          // Update atom with all fetched cards at once
          setFullCardCache((prevCache) => {
            const newCache = new Map(prevCache);
            for (const [id, card] of fetchedCards) {
              newCache.set(id, card);
            }
            return newCache;
          });

          // Persist to IndexedDB
          if (isIndexedDBSupported()) {
            await addToFullCardCache(Array.from(fetchedCards.values()));
          }
        }
      } catch (error) {
        console.error('[useCardLookup] Failed to prefetch cards:', error);
      } finally {
        for (const id of missingIds) {
          fetchingRef.current.delete(id);
        }
      }
    },
    [fullCardCache, setFullCardCache],
  );

  return {
    getCard,
    getFullCard,
    hasFullCard,
    prefetchCard,
    prefetchCards,
    // Also expose raw atoms for advanced usage
    fullCardCache,
    cardIndexById,
  };
}

export default useCardLookup;
